import BreadCrumb from "components/Common/BreadCrumb";
import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import LabelWrapper from "components/Common/LabelWrapper";
import ModalConfirm from "components/Common/ModalConfirm";
import {
  AGENCY_BRANCH_STATUS_OPTION,
  AGENCY_STATUS_OPTION,
  COLOR_AGENCY_BRANCH_STATUS,
  COLOR_AGENCY_STATUS,
  LIST_CHANNEL_OPTION,
  LIST_CHANNEL_USER_OPTION,
  LIST_OPTION_BAR_LIST_OPEN_STATUS,
} from "helpers/constans";
import CountUp from "react-countup";
import { formatDateStringToKorea, formatDateStringToUTC, getPlatFormImage } from "helpers/format";
import TooltipCustom from "components/Common/TooltipCustom";
import { Link } from "react-router-dom";
import CollapseFilter from "components/Common/CollapseFilter";
import ModalMultiPreview from "components/Common/ModalMultiPreview";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import ModalPreview from "components/Common/ModalPreview";
import AgencyForm from "./AgencyForm";
import moment from "moment";
import DatePickerCustom from "components/Common/DatePickerCustom";
import CopyWrapper from "components/Common/CopyWrapper";
import { getAgencyList } from "store/agency/thunk";
import agencyApi from "api/agencyApi";
import { QRCodeCanvas } from "qrcode.react";
import ModalPreviewQRCode from "components/Common/ModalPreviewQRCode";
import NO_IMAGE_USER from "../../assets/images/no-user.png";
import DropdownChannel from "components/Common/DropdownChannel";

interface Option {
  label: string;
  value: string;
}

const TYPE_SELECT_DEFAULT: string = "created_at";

registerLocale("en", en);
registerLocale("ko", ko);

const AgencyList = () => {
  const { t, i18n } = useTranslation();

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    keyword: withDefault(StringParam, ""),
    channel: withDefault(StringParam, ""),
    status: withDefault(StringParam, ""),
    startDate: withDefault(StringParam, ""),
    endDate: withDefault(StringParam, ""),
    // sortBy: withDefault(StringParam, "createdAt"),
    // orderBy: withDefault(StringParam, "desc"),
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [linkImage, setLinkImage] = useState<any>(null);
  const [itemToEdit, setItemToEdit] = useState<any>(false);
  const [previewQRCode, setPreviewQRCode] = useState<any>(null);

  const [dateSearch, setDateSearch] = useState<any[]>([
    query?.startDate
      ? moment(query?.startDate || "", "Y-MM-DD").toDate()
      : null,
    query?.endDate ? moment(query?.endDate || "", "Y-MM-DD").toDate() : null,
  ]);
  const [startDate, endDate] = dateSearch;

  const [keywordSearch, setKeywordSearch] = useState<string>(
    query?.keyword || ""
  );
  const [channelSearch, setChannelSearch] = useState<Option | any>(
    LIST_CHANNEL_OPTION?.find((item) => item?.value === query?.channel)
      ?.value || ""
  );
  const [statusSearch, setStatusSearch] = useState<Option | any>(
    LIST_OPTION_BAR_LIST_OPEN_STATUS?.find(
      (item) => item?.value === query?.status
    )?.value || ""
  );

  const dispatch: any = useDispatch();
  const selectLayoutState = (state: any) => state.Agency;
  const AgencyProperties = createSelector(selectLayoutState, (state) => ({
    lstAgency: state.lstAgency,
    isAgencyLoading: state.isAgencyLoading,
    isAgencySuccess: state.isAgencySuccess,
    error: state.error,
  }));

  // Inside your component
  const { lstAgency, isAgencyLoading } = useSelector(AgencyProperties);

  function loadTableData() {
    dispatch(getAgencyList(query));
  }

  const searchData = () => {
    setQuery({
      ...query,
      keyword: keywordSearch,
      channel: channelSearch,
      status: statusSearch,
      startDate: startDate
        ? formatDateStringToUTC(
          moment(new Date(startDate))
            .startOf("day")
            .format("Y-MM-DD HH:mm:ss")
        )
        : "",
      endDate: endDate
        ? formatDateStringToUTC(
          moment(new Date(endDate)).endOf("day").format("Y-MM-DD HH:mm:ss")
        )
        : "",
      sortBy: TYPE_SELECT_DEFAULT,
      page: 1,
      time_request: +new Date(),
    });
  };

  const resetData = () => {
    setQuery(
      {
        keyword: "",
        app_name: "",
        status: "",
        startDate: "",
        endDate: "",
        sortBy: TYPE_SELECT_DEFAULT,
        time_request: +new Date(),
      },
      "push"
    );

    setKeywordSearch((_prev) => "");
    setStatusSearch((_prev: any) => "");
    setChannelSearch((_prev: any) => "");
    setDateSearch([null, null]);
  };

  const handleOnConfirmUpdate = async (item: {
    id: number | string;
    status: number | string;
  }) => {
    try {
      if (!item) {
        return;
      }
      const response: any = await agencyApi.updateAgency(item?.id, {
        status: item?.status,
      });

      if (response?.code === 200) {
        dispatch(getAgencyList(query));
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };
  // End::Change status

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 80,
        Cell: ({ ...props }) => {
          return (
            <div style={{ minWidth: "60px" }}>
              {lstAgency?.pagination?.total -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("Status"),
        accessor: "status",
        thWidth: 80,
        thClass: "text-center  whitespace-nowrap",
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <div
              className="form-check form-switch form-switch-sm text-center"
              dir="ltr"
            >
              <input
                type="checkbox"
                className="form-check-input"
                name="status"
                checked={cell?.value == "ACTIVE" ? true : false}
                onChange={(e) => {
                  const value = e?.target?.checked;
                  handleOnConfirmUpdate({
                    id: item?.id,
                    status: value == true ? "ACTIVE" : "INACTIVE",
                  });
                }}
              />
            </div>
          );
        },
      },
      {
        Header: t("Linked Branch IO"),
        accessor: "branchLinkStatus",
        thWidth: 150,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              <span
                className={`rounded-pill badge bg-${COLOR_AGENCY_BRANCH_STATUS[cell?.value] || "secondary"
                  }`}
              >
                {t(
                  AGENCY_BRANCH_STATUS_OPTION?.find(
                    (item: any) => item?.value === cell?.value
                  )?.label || ""
                )}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Avatar"),
        accessor: "avatar",
        thClass: "text-center whitespace-nowrap",
        filterable: true,
        sortable: false,
        thWidth: 80,
        Cell: (cell: any) => {
          const photo = cell?.value ?? NO_IMAGE_USER;
          return (
            <>
              <div className="w-100 text-center " style={{ minWidth: "60px" }}>
                <div
                  style={{ width: "45px" }}
                  className="m-auto cursor-pointer"
                  onClick={() => setLinkImage({ url: photo })}
                >
                  <img
                    src={photo}
                    className="rounded-circle avatar-sm"
                    alt="thumbnail"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null; // prevents looping
                      currentTarget.src = NO_IMAGE_USER;
                    }}
                  />
                </div>
              </div>
            </>
          );
        },
      },
      {
        Header: t("Referral Code"),
        accessor: "referralCode",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 140,
        Cell: (cell: any) => {
          return (
            <>
              <div className="cursor-pointer" style={{ minWidth: "120px" }}>
                {cell?.value}
              </div>
            </>
          );
        },
      },
      {
        Header: t("QR Code"),
        accessor: "qr_code",
        thClass: "whitespace-nowrap",
        filterable: true,
        sortable: false,
        thWidth: 120,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;

          let branchLink = item?.branchLink;
          const agencyParams = item?.params;
          if (agencyParams && branchLink) {
            branchLink = `${branchLink}&${agencyParams}`
          }

          return (
            branchLink && (
              <>
                <div
                  className="cursor-pointer"
                  onClick={() => setPreviewQRCode(item)}
                >
                  <QRCodeCanvas value={branchLink} size={80} />
                </div>
              </>
            )
          );
        },
      },
      {
        Header: t("Branch Link"),
        accessor: "branchLink",
        thClass: "whitespace-nowrap",
        filterable: true,
        sortable: false,
        thWidth: 240,
        Cell: (cell: any) => {
          let branchLink = cell?.value;
          // const agencyParams = cell?.row?.original?.params;
          // if (agencyParams && branchLink) {
          //   branchLink = `${branchLink}&${agencyParams}`
          // }

          return (
            branchLink && (
              <div
                className="cursor-pointer text-primary"
                style={{ maxWidth: "220px" }}
              >
                <CopyWrapper
                  contentCopy={branchLink}
                  style={{ minWidth: "70px" }}
                >
                  {(branchLink)}
                </CopyWrapper>
              </div>
            )
          );
        },
      },
      {
        Header: t("Custom Parameters"),
        accessor: "params",
        filterable: false,
        sortable: false,
        thWidth: 180,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const params = cell?.value;

          return (
            params ? (
              <>
                {params.split("&")?.map((item: any, index: number) => {
                  return (
                    <div className="d-flex align-items-center whitespace-nowrap" key={index}>
                      <div>{item}</div>
                    </div>
                  );
                })}
              </>
            ) : "-"
          );
        },
      },
      {
        Header: t("Agency Name"),
        accessor: "agencyName",
        filterable: false,
        sortable: false,
        thWidth: 180,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          const name = cell?.value;
          return (
            <div 
              className="cursor-pointer text-primary"
              onClick={() => {
                setIsOpen(true);
                setItemToEdit(item);
              }}
               style={{ minWidth: 150 }}
              >
                {name}
            </div>
          );
        },
      },
      {
        Header: t("Channel"),
        accessor: "channel",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          let channel = cell?.row?.original?.channel;
          let imgChannel = getPlatFormImage(channel);

          return (
            <div
              className="d-flex flex-column gap-1"
              style={{ minWidth: "100px" }}
            >
              <div className="cursor-pointer d-flex gap-2 align-items-center whitespace-nowrap">
                {imgChannel && (
                  <div style={{ width: "25px" }}>
                    <img
                      src={imgChannel}
                      width={25}
                      height={25}
                      alt="channel"
                    />
                  </div>
                )}

                <div className="cursor-pointer">
                  {t(
                    LIST_CHANNEL_USER_OPTION?.find(
                      (item: any) => String(item?.value) === String(channel)
                    )?.label || (channel || "-")
                  )}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        Header: t("Contact Name"),
        accessor: "contactName",
        filterable: false,
        sortable: false,
        thWidth: 160,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const name = cell?.value;
          return <div style={{ minWidth: 100 }}>{name ?? '-'}</div>;
        },
      },
      {
        Header: t("Contact Phone"),
        accessor: "phone",
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          return (
            <>
              <div className="cursor-pointer">
                {cell?.value ? cell?.value : "-"}
              </div>
            </>
          );
        },
      },
      {
        Header: t("Email"),
        accessor: "email",
        thClass: "whitespace-nowrap",
        thWidth: 200,
        Cell: (cell: any) => {
          return (
            <>
              <div className="cursor-pointer" style={{ minWidth: "60px" }}>
                {cell?.value ? cell?.value : "-"}
              </div>
            </>
          );
        },
      },
      {
        Header: t("Registration date"),
        accessor: "createdAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updatedAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("Button Action"),
        thClass: "text-center",
        thWidth: 150,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul
              className="list-inline d-flex flex-wrap hstack gap-2 mb-0"
              style={{ justifyContent: "center" }}
            >
              <TooltipCustom
                title={t("Button Update")}
                id={`update-md-${item?.id}`}
              >
                <Link
                  className="btn btn-sm btn-soft-primary edit-item-btn"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOpen(true);
                    setItemToEdit(item);
                  }}
                >
                  <i className="ri-pencil-fill align-bottom "></i>
                </Link>
              </TooltipCustom>

              <TooltipCustom
                title={t("Button Delete")}
                id={`delete-md-${item?.id}`}
              >
                <Link
                  className="btn btn-sm btn-soft-danger edit-item-btn"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setItemToDelete(item);
                  }}
                >
                  <i className="ri-delete-bin-3-fill align-bottom"></i>
                </Link>
              </TooltipCustom>
            </ul>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, lstAgency?.pagination?.total, query?.page]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePicker = (values: any[] = []) => {
    setDateSearch((_prev: any) => values);
  };

  // Begin::Delete
  const handleActionDelete = async (id: any) => {
    if (!id) {
      return;
    }
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await agencyApi?.deleteAgency(id);
      if (response?.code === 200 && response?.message === "Success") {
        setItemToDelete(false);
        loadTableData();
        setIsConfirmLoading((_prev) => false);
        toast(
          `${t("The agency has been deleted.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const onCloseImageClick = () => {
    setLinkImage(null);
  };

  const onCloseQRCodeClick = () => {
    setPreviewQRCode(null);
  };

  const onCloseClick = () => {
    setItemToEdit((_prev: any) => null);
    setIsOpen((_prev) => false);
  };

  const triggerRefresh = () => {
    onCloseClick();
    loadTableData();
  };

  useEffect(() => {
    loadTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(query)]);

  useEffect(() => {
    document.title = `${t("Agency Management")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Agency Management")} pageTitle={t("Home")} />
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <div className="d-block d-xl-flex justify-content-between align-items-start">
                    <div
                      className="d-flex w-100 flex-wrap gap-3 gap-md-2 menu-card-statistics justify-content-start mb-3 mb-xl-2 position-relative"
                      style={{ maxWidth: "100%" }}
                    >
                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={lstAgency?.pagination?.total || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total Active")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={lstAgency?.summary?.totalActive || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total InActive")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={lstAgency?.summary?.totalInactive || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>

                    <div className="col-sm-auto text-end">
                      <button
                        type="button"
                        className="btn btn-success d-md-block"
                        onClick={() => {
                          setIsOpen(true);
                          setItemToEdit(null);
                        }}
                      >
                        <i className="ri-add-fill align-bottom me-1"></i>{" "}
                        {t("Button Create Agency")}
                      </button>
                    </div>
                  </div>
                </CardHeader>

                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-0 mt-md-2">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper
                          label={t("Keyword")}
                          isShow={!!keywordSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Keyword")}...`}
                            value={keywordSearch}
                            onChange={(e) => setKeywordSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("Channel")} isShow={true}>
                          <DropdownChannel
                            name="channel"
                            placeholder={`${t("Channel")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={channelSearch}
                            onChangeSelect={(e: any) => {
                              setChannelSearch(e?.value);
                            }}
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("Status")} isShow={true}>
                          <DropdownStatusNew
                            name="status"
                            dataList={AGENCY_STATUS_OPTION || []}
                            placeholder={`${t("Select Status")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={statusSearch}
                            onChangeSelect={(e: any) =>
                              setStatusSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_AGENCY_STATUS}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-sm-4 mt-md-2 mt-3 date-picker-wrapper-custom"
                      >
                        <LabelWrapper
                          label={t("Date")}
                          isShow={!!startDate || !!endDate}
                        >
                          <DatePickerCustom
                            startDate={startDate || null}
                            endDate={endDate || null}
                            onChangePicker={handleChangePicker}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={12}
                        xl={12}
                        xxl={12}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-3 mt-md-3"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div>
                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed sticky-table-recruiter-media"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={lstAgency?.items?.length ? lstAgency?.items : []}
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={lstAgency?.pagination?.total}
                      customPageCount={Math.ceil(
                        Number(lstAgency?.pagination?.total) / Number(query.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sortBy: query.sortBy,
                        orderBy: query.orderBy,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isAgencyLoading}
                    />
                  </div>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {isOpen && (
          <AgencyForm
            setIsOpen={setIsOpen}
            id={itemToEdit?.id}
            setItemToEdit={setItemToEdit}
            triggerRefresh={triggerRefresh}
            setLinkImage={setLinkImage}
          />
        )}

        {itemToDelete && (
          <ModalConfirm
            header={t("Delete Agency Information")}
            isOpen={itemToDelete}
            isLoading={isConfirmLoading}
            onClose={() => setItemToDelete(false)}
            onConfirm={() => handleActionDelete(itemToDelete?.id)}
          />
        )}

        <ModalPreview
          type={linkImage?.isVideo ? "VIDEO" : "IMAGE"}
          isOpen={!!linkImage?.url}
          url={linkImage?.url}
          content={linkImage?.content}
          onClose={() => onCloseImageClick()}
        />

        <ModalMultiPreview
          isOpen={!!linkImage?.urls}
          urls={linkImage?.urls || []}
          content={linkImage?.content}
          onClose={() => onCloseImageClick()}
        />

        {previewQRCode && (
          <ModalPreviewQRCode
            itemQRCode={previewQRCode}
            onCloseQRCodeClick={onCloseQRCodeClick}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default AgencyList;
